import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  X, ChevronRight, ChevronLeft, FileText, Shield, User, Building2, 
  Sparkles, CheckCircle, Rocket, Target, Zap 
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const OnboardingTour = ({ onComplete, user }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const steps = [
    {
      id: 'welcome',
      icon: Sparkles,
      title: 'Bienvenue sur SmartFacture AI !',
      titleEn: 'Welcome to SmartFacture AI!',
      description: `Bonjour ${user?.full_name || 'là'} ! Découvrez comment notre plateforme double-IA va transformer votre business.`,
      descriptionEn: `Hello ${user?.full_name || 'there'}! Discover how our dual-AI platform will transform your business.`,
      color: 'from-emerald-500 to-emerald-500',
      tips: [
        'SmartFacture AI pour la facturation intelligente',
        'LocalMod AI pour la modération de contenu',
        'Tableau de bord unifié pour tout gérer'
      ],
      tipsEn: [
        'SmartFacture AI for intelligent invoicing',
        'LocalMod AI for content moderation',
        'Unified dashboard to manage everything'
      ]
    },
    {
      id: 'smartfacture',
      icon: FileText,
      title: 'SmartFacture AI',
      titleEn: 'SmartFacture AI',
      description: 'Créez des factures professionnelles en quelques clics grâce à l\'IA GPT-5.2.',
      descriptionEn: 'Create professional invoices in a few clicks with GPT-5.2 AI.',
      color: 'from-emerald-500 to-emerald-600',
      tips: [
        'Génération automatique de factures',
        'Rappels de paiement intelligents',
        'Export PDF professionnel avec votre logo',
        'Multi-devises (EUR, USD, GBP)'
      ],
      tipsEn: [
        'Automatic invoice generation',
        'Smart payment reminders',
        'Professional PDF export with your logo',
        'Multi-currency (EUR, USD, GBP)'
      ],
      action: '/dashboard/smartfacture'
    },
    {
      id: 'localmod',
      icon: Shield,
      title: 'LocalMod AI',
      titleEn: 'LocalMod AI',
      description: 'Modérez automatiquement le contenu de votre plateforme avec Claude Sonnet 4.5.',
      descriptionEn: 'Automatically moderate your platform content with Claude Sonnet 4.5.',
      color: 'from-emerald-500 to-emerald-600',
      tips: [
        'Détection de toxicité et spam',
        'Support de 5+ langues',
        'API REST simple à intégrer',
        'Analyse en temps réel'
      ],
      tipsEn: [
        'Toxicity and spam detection',
        '5+ languages supported',
        'Simple REST API to integrate',
        'Real-time analysis'
      ],
      action: '/dashboard/localmod'
    },
    {
      id: 'profile',
      icon: User,
      title: 'Complétez votre profil',
      titleEn: 'Complete your profile',
      description: 'Personnalisez votre expérience et configurez votre entreprise.',
      descriptionEn: 'Personalize your experience and configure your business.',
      color: 'from-green-500 to-emerald-500',
      tips: [
        'Ajoutez votre logo pour les factures',
        'Configurez vos informations d\'entreprise',
        'Personnalisez vos préférences de branding',
        'Choisissez votre langue préférée'
      ],
      tipsEn: [
        'Add your logo for invoices',
        'Configure your business information',
        'Customize your branding preferences',
        'Choose your preferred language'
      ],
      action: '/dashboard/profile'
    },
    {
      id: 'ready',
      icon: Rocket,
      title: 'Vous êtes prêt !',
      titleEn: 'You\'re ready!',
      description: 'Votre compte est configuré. Commencez à automatiser votre business dès maintenant.',
      descriptionEn: 'Your account is set up. Start automating your business now.',
      color: 'from-amber-500 to-orange-500',
      tips: [
        'Créez votre première facture',
        'Testez la modération de contenu',
        'Explorez les métriques business',
        'Générez vos clés API'
      ],
      tipsEn: [
        'Create your first invoice',
        'Test content moderation',
        'Explore business metrics',
        'Generate your API keys'
      ]
    }
  ];

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const isLastStep = currentStep === steps.length - 1;
  const Icon = currentStepData.icon;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <Card className="w-full max-w-2xl shadow-2xl border-0 overflow-hidden animate-in zoom-in-95 duration-300 bg-white">
        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100">
          <div 
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <CardHeader className="bg-gradient-to-br from-emerald-50 to-emerald-50 text-slate-800 pb-8 pt-6 border-b border-slate-100">
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Icon className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <Badge className="bg-emerald-100 text-emerald-700 border-0 mb-2">
                  Étape {currentStep + 1} / {steps.length}
                </Badge>
                <CardTitle className="text-2xl font-bold text-slate-800">
                  {currentStepData.title}
                </CardTitle>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleSkip}
              className="text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <CardDescription className="text-slate-600 text-base mt-4">
            {currentStepData.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="p-6">
          <div className="space-y-3 mb-6">
            {currentStepData.tips.map((tip, idx) => (
              <div 
                key={idx} 
                className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg animate-in slide-in-from-left duration-300"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-700">{tip}</span>
              </div>
            ))}
          </div>

          {/* Step indicators */}
          <div className="flex justify-center space-x-2 mb-6">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-500 w-8' 
                    : idx < currentStep 
                      ? 'bg-green-500' 
                      : 'bg-slate-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="text-slate-600"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleSkip}
                className="text-slate-600"
              >
                Passer le tour
              </Button>
              <Button
                onClick={handleNext}
                className={`bg-gradient-to-r ${currentStepData.color} hover:opacity-90 text-white px-6`}
              >
                {isLastStep ? (
                  <>
                    <Rocket className="h-4 w-4 mr-2" />
                    Commencer
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTour;
